// Client-side instrumentation: error monitoring (Sentry) + product analytics
// (PostHog). Both are KEY-GATED no-ops until their env var is set, so this file
// is inert in local/dev until you opt in — same pattern across the app.

import * as Sentry from '@sentry/nextjs'
import posthog from 'posthog-js'
import { REPLAY_BLOCK_SELECTOR, AUTOCAPTURE_ATTRIBUTE_DENYLIST } from './lib/analytics/replayMask'
import {
  scrubPostHogEvent,
  scrubSentryBreadcrumb,
  scrubSentryEvent,
  maskReplayAttribute,
  maskReplayUrl,
} from './lib/analytics/scrubEvent'

// ── Sentry (NEXT_PUBLIC_SENTRY_DSN) — see instrumentation.ts ──
const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN
if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.NEXT_PUBLIC_VERCEL_ENV || 'development',
    tracesSampleRate: 0.1,
    // Where someone is rides in every breadcrumb (a navigation's from/to), the
    // request url and the transaction's name — and on the stage a place's url
    // holds the slug of a private title. Templated, and auth tokens out of the
    // hash, before anything leaves (lib/analytics/scrubEvent).
    beforeBreadcrumb: scrubSentryBreadcrumb,
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
  })
}

// ── PostHog (NEXT_PUBLIC_POSTHOG_KEY) ──
// Ingestion is proxied first-party through the /ingest rewrite (next.config), so
// events post to innernet.live (ad-blocker-safe, nothing third-party in the URL
// bar). `defaults: '2026-05-30'` turns on automatic $pageview + $pageleave that
// follow client-side App Router navigation, plus autocapture of clicks — so we
// get "who came from where" (UTM + referrer on every pageview) and "what they
// did" without hand-instrumenting each page.
//
// Privacy-first choices, on brand: person profiles only for identified users
// (anonymous visitors are still counted + attributed, but don't get a persistent
// profile until they sign in), and session replay masked to the bone (below).
// Location is scrubbed before it leaves, in lib/analytics/scrubEvent.ts (apart
// from here: this module calls posthog.init() on import, so nothing can test it).
// Two things ride in a url that must not reach PostHog:
//   • auth credentials — a PKCE `?code=` or a magic-link / implicit
//     `#access_token=…` (see AuthHashCatch, which runs on every page). $pageview
//     captures the full href *including the hash*, so the hash goes wholesale,
//     and so does a denylist of sensitive query keys — while utm_* stays (that
//     attribution is the whole point).
//   • places — on the stage every directory location has its own address, and a
//     place's address ends in the slug of a dimension's title. `defaults`
//     below sends one $pageview per pushState, so without this every move
//     between folders would ship a private title. A place leaves as its template
//     (/dashboard/projects/:project/:place, lib/analytics/placePath): pageviews
//     stay ON, they just no longer say which place. Moving between places makes
//     more pageviews with the same address; they are not thinned out here,
//     because dropping a $pageview in before_send orphans the $pageview_id the
//     events after it point at.

// ── session replay sampling ──────────────────────────────────────────────────
// Replay is the expensive signal — one recording is orders of magnitude more
// data than one event, and PostHog bills per recording. So we keep a MODEST
// slice: enough that there are always a few real journeys to watch, not enough
// to burn the quota in a fortnight. At innernet's traffic 20% is a handful of
// sessions a day, which is the right number for a human to actually sit and
// watch. Raise it deliberately for a specific investigation, then put it back —
// a permanently high rate is a bill, not an insight.
const REPLAY_SAMPLE_RATE = 0.2

// The block list lives in lib/analytics/replayMask.ts — apart from this file
// because this module calls posthog.init() on import, and a privacy guarantee
// this load-bearing has to be readable (and testable) without starting
// analytics to do it. The reasoning for blocking rather than masking is there.

// The verdict has to be STICKY for a whole session. Roll the dice per page load
// and a reload re-rolls it, leaving a pile of ten-second fragments instead of a
// few whole journeys — which is the one thing replay is for. Deriving it from
// PostHog's own session id gives stickiness for free: same id → same verdict,
// and it re-rolls exactly when the session rotates. No extra storage, nothing
// new for a person to clear.
function inReplaySample(sessionId: string | null | undefined): boolean {
  // a session that is resetting hands over no id (null): not in the sample.
  // Reading .length on it was the "Cannot read properties of null (reading
  // 'length')" in error tracking, 2026-09-08 → 27.
  if (!sessionId) return false
  let h = 0x811c9dc5 // FNV-1a, 32-bit — cheap, and evenly spread over uuids
  for (let i = 0; i < sessionId.length; i++) {
    h ^= sessionId.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0) / 0x100000000 < REPLAY_SAMPLE_RATE
}

const phKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
if (phKey && typeof window !== 'undefined') {
  posthog.init(phKey, {
    api_host: '/ingest',
    // where the toolbar / "view in PostHog" links point (US cloud; use
    // https://eu.posthog.com if your project is in the EU region).
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || 'https://us.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    // COOKIELESS — keeps the "no tracking cookies, no cookie banner" promise
    // (see /privacy). Identity persists in localStorage, not a cookie.
    persistence: 'localStorage',
    // ── autocapture, with the content taken out ──
    // Autocapture is on (it comes with `defaults`), and by default it ships the
    // textContent AND the attributes of whatever was clicked. In this app a
    // clicked row is a dimension, so `$el_text` was carrying the very memory
    // names session replay refuses to record — the strict half of the system
    // was being undone by the casual half. Text off, and the attributes that
    // hold memory or a person's own words dropped by name.
    mask_all_text: true,
    autocapture: {
      element_attribute_ignorelist: [...AUTOCAPTURE_ATTRIBUTE_DENYLIST],
    },
    // ── no feature flags ──
    // Nothing in this app reads a flag, and the /flags request is the one call
    // that never passes before_send: it posts the person's FIRST url and
    // referrer raw ($initial_person_info, stored by the first capture after init
    // or after a sign-out's reset()). After a sign-out that first page can be
    // `/login?next=<a place>`, and the identify() at sign-in would send it on.
    // Off, so the request is never made (1.422.5: reloadFeatureFlags returns
    // before any request; its docs still say "/flags keeps running", so look
    // again on an upgrade). NOT `advanced_disable_flags`: that one also stops the
    // remote config (/array/<key>/config, no person data in it), and replay never
    // starts without it (`_isRecordingEnabled` needs the server's `enabled`). The
    // day a flag is wanted, template persistence's $initial_person_info first.
    advanced_disable_feature_flags: true,
    // Console capture defaults to the PROJECT's remote setting, which is on —
    // so a stray console.log of a fold result or a fetch response would land
    // in the recording having passed no mask at all. Off, explicitly, here
    // where it can be seen next to everything else it has to agree with.
    enable_recording_console_log: false,
    // ── session replay: ON, masked to the bone ──
    // Watching where a real person stalls is the one thing an event funnel
    // cannot tell you — a funnel says they dropped at "connect", replay says
    // they scrolled past the button three times. That is why it's on.
    //
    // It is allowed under the no-cookie law because replay sets NO COOKIES. It
    // has no storage of its own: the session and window ids it needs come from
    // the persistence chosen above (localStorage + sessionStorage), and rrweb —
    // the recorder itself — only reads the DOM. With `persistence:
    // 'localStorage'` there is no cookie path anywhere in the capture.
    //
    // What we record is the SHAPE of a session, never its content. Every text
    // node and every input is masked in the page, BEFORE anything is sent — the
    // unmasked pixels never exist on the wire or at PostHog. A recording shows
    // layout, cursor, scroll and clicks; it cannot show a memory, a note, an
    // email address or a password.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true, // every input/textarea/select, no exceptions
      maskTextSelector: '*', // every text node, including our own copy
      // Anything a surface marks as too sensitive to even outline is dropped
      // wholesale — the element is replaced by a placeholder box. `blockClass`
      // is already the posthog-js default; stating it makes the contract with
      // the rest of the app explicit rather than inherited.
      blockClass: 'ph-no-capture',
      // Every surface that renders someone's memory, or carries it in an
      // attribute — and every live canvas, which rrweb would otherwise record
      // call by call until the page hangs. Blocked, not masked: a blocked
      // element is replaced by a placeholder before serialisation, so neither
      // its text NOR its attributes are ever recorded. The list, and why, is
      // in replayMask.ts.
      blockSelector: REPLAY_BLOCK_SELECTOR,
      // Never capture network payloads. Bodies are precisely where the memory
      // content lives — MCP payloads, dimension bodies, auth responses — so
      // capturing them would undo every mask above.
      recordHeaders: false,
      recordBody: false,
      // Our fonts are already on the page at replay time; shipping them with
      // every recording only inflates it.
      collectFonts: false,
      // The recording's own record of WHERE it is. rrweb stores attributes
      // verbatim, so `<a href="/dashboard/projects/<id>/<title-slug>">` in the
      // chrome would put a place's title in the recording (the block list keeps
      // memory rows out, not the links to them), and so would a navigator
      // word's `data-nav-key` or a control's `aria-label` ("open <skill>"); and
      // the recorder passes the page's url through the network mask for every
      // Meta event it writes.
      //
      // ⚠️ The price, paid on purpose: with ANY maskAttributeFn set, rrweb stops
      // shrinking an inline-style change to its diff (posthog-js 1.422.5,
      // lazy-recorder MutationBuffer: `!maskAllElementAttributes &&
      // !maskAttributeFn`). It asks whether a function exists, not what it
      // returns, so one that hands `style` back untouched buys nothing back.
      // Every style write is recorded whole, and <html>'s style is rewritten on
      // every step of the read ramp as you scroll: ~1KB a step where a diff was
      // ~100B. That is bytes (gzipped, throttled per element, and replay is
      // billed per recording). Blocking the place links instead would keep the
      // diffs, but only for the attributes someone remembered to list; this one
      // reads every attribute, the ones not written yet included.
      maskAttributeFn: maskReplayAttribute,
      maskCapturedNetworkRequestFn: maskReplayUrl,
    },
    // last line of defence: every location property (not only the pageview's —
    // the session's entry url and the person's initial url ride on every event),
    // the autocapture chain a clicked link writes its href into, and any string
    // that names a place. Auth tokens out, places templated: scrubEvent.ts.
    before_send: scrubPostHogEvent,
  })

  // Apply the replay sample. Registered synchronously, immediately after init:
  // persistence is restored during init, so `onSessionId` fires right here with
  // the current id and an out-of-sample session is stopped in the same tick it
  // started — no orphan fragment gets uploaded. The same listener then re-runs
  // on every session rotation, which is what makes the verdict re-roll cleanly
  // instead of being frozen for the life of the tab.
  //
  // Sampling lives HERE rather than in the PostHog project settings so it sits
  // next to the reasoning above and moves with the code. Leave the project-side
  // sample rate at 100% or the two will multiply.
  posthog.onSessionId((sessionId) => {
    if (inReplaySample(sessionId)) {
      if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording()
    } else if (posthog.sessionRecordingStarted()) {
      posthog.stopSessionRecording()
    }
  })
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
